import { lazy, RefObject, useCallback, useEffect } from "react"
import { PaperPlaneTilt } from "@phosphor-icons/react"
import { ConnectionHandler, ROOT_ID } from "relay-runtime"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Button } from "@/components/ui/button"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useHotkeys } from "@/hooks/useHotkeys"
import { cn } from "@/lib/utils"
import { useClearChatHistory, useReadChatHistory } from "@/modules/chats/hooks/useChatHistory"
import { buildMessageOriginalContent } from "@/modules/chats/lib/build-message-original-content"
import { ChatMessagesConnectionKey } from "@/modules/chats/services/useChatMessagesQuery"
import { useSendChatMessageMutation } from "@/modules/chats/services/useSendChatMessageMutation"
import { useChatInputContext } from "@/modules/chats/stores/useChatInputStore"
import { SendChatMessage } from "@/modules/chats/types/chat-message"
import { ChatThread } from "@/modules/chats/types/chat-thread"
import { SofiaEnvironment } from "@/sofia-environment"

const ChatSendPatientMessage = lazy(() =>
  import("@/modules/chats/components/chat-input/chat-send-patient-message").then(({ ChatSendPatientMessage }) => ({
    default: ChatSendPatientMessage,
  })),
)

type SendMessageButtonsProps = {
  selectedThread: ChatThread
  textareaRef: RefObject<HTMLTextAreaElement | null>
}

export const SendMessageButtons = (props: SendMessageButtonsProps) => {
  const { selectedThread, textareaRef } = props

  const isAiRunning = useChatInputContext((store) => store.aiRunning !== "NONE")
  const aiTrackingMetadata = useChatInputContext((store) => store.aiTrackingMetadata)
  const messageText = useChatInputContext((store) => store.messageText)

  const setAiTrackingMetadata = useChatInputContext((store) => store.setAiTrackingMetadata)
  const setMessageText = useChatInputContext((store) => store.setMessageText)
  const setIsSendingMessage = useChatInputContext((store) => store.setIsSendingMessage)

  const readChatHistory = useReadChatHistory()
  const clearChatHistory = useClearChatHistory()

  const [sendMessage, isSendMessageInFlight] = useSendChatMessageMutation()

  useEffect(() => {
    setIsSendingMessage(isSendMessageInFlight)
  }, [isSendMessageInFlight, setIsSendingMessage])

  const isSendButtonDisabled = isAiRunning || messageText.trim().length === 0 || isSendMessageInFlight

  const commitChatMessage = useCallback(
    (newMessage: SendChatMessage, onMessageSentSuccessfully: () => void) => {
      const threadId = selectedThread.id
      const connectionId = ConnectionHandler.getConnectionID(ROOT_ID, ChatMessagesConnectionKey, { threadId })
      const aiFeatures = newMessage.aiFeatures.length > 0 ? JSON.stringify(newMessage.aiFeatures) : null

      sendMessage({
        variables: {
          connections: [connectionId],
          input: {
            content: newMessage.sendContent,
            threadId,
            originalContent: newMessage.originalContent,
            aiFeatures,
            aiTrackingMetadata:
              newMessage.aiTrackingMetadata && newMessage.aiTrackingMetadata.length > 0
                ? JSON.stringify(newMessage.aiTrackingMetadata)
                : null,
          },
        },
        onCompleted: (response) => {
          const message = response.sendChatMessage?.sendChatMessageOutput?.message
          if (!message) return

          onMessageSentSuccessfully()
        },
      })
    },
    [sendMessage, selectedThread.id],
  )

  const handleSendMessage = useCallback(() => {
    if (isSendButtonDisabled) return

    const history = readChatHistory()
    const currentMessage = history.at(-1)

    const message: SendChatMessage = {
      originalContent: buildMessageOriginalContent(currentMessage?.contentBeforeAiFeature),
      sendContent: messageText,
      aiFeatures: currentMessage?.aiFeatures ?? [],
      aiTrackingMetadata: aiTrackingMetadata,
    }

    commitChatMessage(message, () => {
      FaroInsightsWrapper.pushEvent("chat_message_sent", {
        chatThreadId: selectedThread.id,
        patientId: selectedThread.patient.id,
        appointmentId: selectedThread.appointmentId,
      })

      setAiTrackingMetadata(null)
      setMessageText("")
      clearChatHistory()
    })
  }, [
    aiTrackingMetadata,
    isSendButtonDisabled,
    messageText,
    selectedThread,
    readChatHistory,
    clearChatHistory,
    commitChatMessage,
    setAiTrackingMetadata,
    setMessageText,
  ])

  useHotkeys("ctrl+enter", handleSendMessage, { ref: textareaRef })

  return (
    <div className="flex h-12 items-center justify-center">
      {SofiaEnvironment.AllowDebugFeatures && (
        <ChatSendPatientMessage
          chatThreadId={selectedThread.id}
          isSendButtonDisabled={isSendButtonDisabled}
          messageText={messageText}
          patientId={selectedThread.patient.id}
        />
      )}
      <Button
        variant="circle"
        data-testid="send-chat-message-button"
        className="h-8 w-8 disabled:bg-transparent"
        onClick={isSendButtonDisabled ? undefined : handleSendMessage}
        disabled={isSendButtonDisabled}>
        {isAiRunning ? (
          <LoadingSpinner size={24} />
        ) : (
          <PaperPlaneTilt size={24} className={cn({ "text-on-surface-disabled": isSendButtonDisabled })} />
        )}
      </Button>
    </div>
  )
}
